import { useCallback, useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'

import { apiFetch } from '@/lib/api'
import {
  detectPushCapability,
  getCurrentSubscription,
  type PushCapability,
  type PushSubscriptionPayload,
} from '@/lib/push'

export function useVapidPublicKey() {
  return useQuery({
    queryKey: ['vapid-public-key'],
    queryFn: () =>
      apiFetch<{ publicKey: string }>('/api/auth/vapid-public-key').then(
        (r) => r.publicKey,
      ),
    staleTime: Infinity,
    retry: false,
  })
}

export function useRegisterPushSubscription() {
  return useMutation({
    mutationFn: (sub: PushSubscriptionPayload) =>
      apiFetch<{ ok: true }>('/api/auth/push-subscribe', {
        method: 'POST',
        body: { ...sub },
      }),
  })
}

export function useUnregisterPushSubscription() {
  return useMutation({
    mutationFn: (endpoint: string) =>
      apiFetch<null>('/api/auth/push-subscribe', {
        method: 'DELETE',
        body: { endpoint },
      }),
  })
}

export function useTestPush() {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ sent: number; removed: number }>('/api/admin/push-test', {
        method: 'POST',
      }),
  })
}

export function usePushSubscription() {
  const [capability, setCapability] = useState<PushCapability>({
    supported: false,
    iosStandaloneRequired: false,
    permission: 'default',
  })
  const [hasSubscription, setHasSubscription] = useState(false)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    setLoading(true)
    const cap = detectPushCapability()
    setCapability(cap)
    if (cap.supported && cap.permission === 'granted') {
      const sub = await getCurrentSubscription()
      setHasSubscription(!!sub)
    } else {
      setHasSubscription(false)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { capability, hasSubscription, loading, refresh }
}
